import { useToast } from '@/hooks/use-toast';
import {
  useKycAdminEditFormSubmission,
  useKycFormSubmission,
} from '@/hooks/useKyc';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { extractErrorMessage } from '@/lib/errorUtils';
import { LocalKycFormData, adaptKycFormDataForApi } from '@/lib/kycFormAdapter';
import { useCallback, useMemo } from 'react';
import { FormValidationRef, KycFormData } from '../types/kycFormTabs.types';
import { isFormComplete } from '../utils/kycFormTabs.utils';

interface UseKycFormSubmissionHookOptions {
  token: string;
  formData: KycFormData;
  communicationLanguage: string;
  setCommunicationLanguageError: (error: string) => void;
  requiredFormIds: string[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  identificationFormRef: React.RefObject<FormValidationRef | null>;
  formKRef: React.RefObject<FormValidationRef | null>;
  formARef: React.RefObject<FormValidationRef | null>;
  onSubmit?: (data: KycFormData) => void;
  onError?: (error: Error) => void;
  /** Whether to use admin edit submission endpoint */
  isAdminEdit?: boolean;
}

/**
 * Custom hook for managing KYC form submission logic.
 * Handles validation, API submission, and error handling.
 * Supports both regular and admin edit submission modes.
 */
export function useKycFormSubmissionHook({
  token,
  formData,
  communicationLanguage,
  setCommunicationLanguageError,
  requiredFormIds,
  activeTab,
  setActiveTab,
  identificationFormRef,
  formKRef,
  formARef,
  onSubmit,
  onError,
  isAdminEdit = false,
}: UseKycFormSubmissionHookOptions) {
  const { toast } = useToast();
  const tForms = useFormsTranslation();

  // Use appropriate submission hook based on mode
  const regularSubmission = useKycFormSubmission();
  const adminEditSubmission = useKycAdminEditFormSubmission();
  const kycFormSubmission = isAdminEdit
    ? adminEditSubmission
    : regularSubmission;

  /**
   * Check if all required forms are complete and ready for submission.
   * Memoized to prevent redundant calculations.
   */
  const areAllFormsComplete = useMemo((): boolean => {
    const tFormsFunc = (key: string) => tForms.t(key);
    const formsComplete = requiredFormIds.every(formId =>
      isFormComplete(formId, formData, tFormsFunc)
    );

    return formsComplete && !!communicationLanguage;
  }, [requiredFormIds, formData, communicationLanguage, tForms]);

  /**
   * Submit form data to API after adapting to backend format.
   * Handles API submission with proper error handling and user feedback.
   * Passes requiredFormIds to ensure hidden forms send null values for cleanup.
   */
  const submitToApi = useCallback(
    async (data: KycFormData) => {
      try {
        // Adapt the form data to API format, passing requiredFormIds
        // so hidden forms explicitly send null values for backend cleanup
        const apiFormData = adaptKycFormDataForApi(
          token,
          data as LocalKycFormData,
          requiredFormIds
        );

        // Submit to API using the mutation hook
        kycFormSubmission.mutate(apiFormData, {
          onSuccess: _response => {
            toast({
              title: tForms.t('kyc.submission.success.title'),
              description: tForms.t('kyc.submission.success.description'),
            });

            onSubmit?.(data);
          },
          onError: error => {
            console.error('KYC form submission error:', error);
            const errorMessage = extractErrorMessage(
              error,
              'Failed to submit KYC form'
            );

            toast({
              title: tForms.t('kyc.submission.error.title'),
              description: errorMessage,
              variant: 'destructive',
            });

            onError?.(error as Error);
          },
        });
      } catch (error) {
        console.error('Error preparing form data for API:', error);
        const errorMessage = extractErrorMessage(
          error,
          'Failed to prepare form data'
        );

        toast({
          title: tForms.t('kyc.submission.preparation.error'),
          description: errorMessage,
          variant: 'destructive',
        });
      }
    },
    [
      token,
      kycFormSubmission,
      toast,
      tForms,
      onSubmit,
      onError,
      requiredFormIds,
    ]
  );

  /**
   * Manual form submission handler with hybrid validation.
   * Validates all required forms and submits if all are valid.
   */
  const handleManualSubmit = useCallback(async () => {
    let allFormsValid = true;
    let firstInvalidForm = '';

    // Validate communication language first
    if (!communicationLanguage) {
      setCommunicationLanguageError(
        tForms.t('kyc.communicationLanguage.validation.required')
      );
      return;
    }

    /**
     * Helper to validate a form using either ref or data validation.
     */
    const validateForm = async (
      formId: string,
      formRef: React.RefObject<FormValidationRef | null>
    ): Promise<boolean> => {
      if (activeTab === formId) {
        // Use ref validation for active form
        return (await formRef.current?.triggerValidation()) ?? false;
      }
      // Use data validation for inactive form
      const tFormsFunc = (key: string) => tForms.t(key);
      return isFormComplete(formId, formData, tFormsFunc);
    };

    // Validate identification form
    if (requiredFormIds.includes('identification')) {
      const isValid = await validateForm(
        'identification',
        identificationFormRef
      );
      if (!isValid) {
        allFormsValid = false;
        firstInvalidForm = 'identification';
      }
    }

    // Validate Form K if required
    if (requiredFormIds.includes('form-k')) {
      const isValid = await validateForm('form-k', formKRef);
      if (!isValid) {
        allFormsValid = false;
        firstInvalidForm = firstInvalidForm || 'form-k';
      }
    }

    // Validate Form A if required
    if (requiredFormIds.includes('form-a')) {
      const isValid = await validateForm('form-a', formARef);
      if (!isValid) {
        allFormsValid = false;
        firstInvalidForm = firstInvalidForm || 'form-a';
      }
    }

    // If validation failed, switch to first invalid form
    if (!allFormsValid && firstInvalidForm) {
      setActiveTab(firstInvalidForm);
      return;
    }

    // If all forms are valid, proceed with API submission
    if (allFormsValid) {
      submitToApi(formData);
    }
  }, [
    requiredFormIds,
    formData,
    submitToApi,
    activeTab,
    communicationLanguage,
    setCommunicationLanguageError,
    tForms,
    identificationFormRef,
    formKRef,
    formARef,
    setActiveTab,
  ]);

  return {
    areAllFormsComplete,
    handleManualSubmit,
    submitToApi,
    kycFormSubmission,
  };
}
